/**
 * Values a field writes by itself, as its descriptor asks (`Field.auto_select_single`,
 * `Field.siret_fills`). Generic: the server says which fields, the client only applies it.
 */

type SelectableOption = { value: string; disabled?: boolean };

/** An option the user may choose: listed, not `disabled`, and not the empty choice. */
function isSelectable(option: SelectableOption): boolean {
    return !option.disabled && option.value !== "" && option.value !== "__null__";
}

/** The value of the only selectable option, `null` when there is none or several. */
export function singleSelectableOption(options: SelectableOption[]): string | null {
    const selectable = options.filter(isSelectable);
    return selectable.length === 1 ? selectable[0].value : null;
}

/**
 * `auto_select_single` on a loaded list: the value to write, `undefined` to leave the field as it is.
 * A value still offered as selectable stays; one the list no longer offers, or offers `disabled` (it
 * followed another choice), is replaced by the only selectable option, or cleared (`""`), an empty list
 * included.
 */
export function autoSelection(current: unknown, options: SelectableOption[]): string | undefined {
    const filled = current !== null && current !== undefined && String(current) !== "";
    if (filled && options.some(option => isSelectable(option) && option.value === String(current))) return undefined;
    const single = singleSelectableOption(options);
    if (single !== null) return single;
    return filled ? "" : undefined;
}

/**
 * Dependent options (`options_params` with a `$field` value): the list follows another value of the
 * form, so a selection the reloaded list no longer offers is dropped (`keepAvailableSelection`).
 * Not for `auto_select_single` (it has its own rule, `autoSelection`) nor for a list searched as the
 * user types (`options_search_param`: the selection is rarely in the page loaded).
 */
export function dropsUnofferedSelection(field: {
    options_params?: Record<string, string> | null;
    auto_select_single?: boolean | null;
    options_search_param?: string | null;
    keep_offered_only?: boolean | null;
}): boolean {
    if (field.auto_select_single || field.options_search_param) return false;
    // The server says the list served is the only valid one (`keep_offered_only`, set by `form-step` on the
    // options a step serves), whatever its source; or the list follows another value of the form.
    if (field.keep_offered_only) return true;
    return Object.values(field.options_params ?? {}).some(value => typeof value === "string" && value.startsWith("$"));
}

/**
 * The values the Sirene establishment `found` gives to the fields of `fills`
 * (`{field_name: [attribute, …]}`): for each field, the first attribute filled.
 */
export function sireneFillValues(fills: Record<string, string[]> | null | undefined, found: Record<string, unknown> | null | undefined): Record<string, string> {
    const values: Record<string, string> = {};
    if (!fills || !found) return values;
    Object.entries(fills).forEach(([name, attributes]) => {
        for (const attribute of attributes) {
            const value = found[attribute];
            if (value !== null && value !== undefined && String(value).trim() !== "") {
                values[name] = String(value).trim();
                break;
            }
        }
    });
    return values;
}

/**
 * Whether a fill may write `name`: always once the SIRET was typed by the user, otherwise (a SIRET
 * prefilled, a step shown again) only into an empty field, so what is there is never lost.
 */
export function mayFill(typed: boolean, current: unknown): boolean {
    return typed || current === null || current === undefined || String(current).trim() === "";
}
